import React, { Dispatch, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DialogHeader from '../_parts/DialogHeader';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../redux/store';
import DialogFrame from '../_parts/DialogFrame';
import { useParams } from 'react-router-dom';
import { DialogActions, DialogContent, Divider } from '@mui/material';
import { ContentLayout } from '../_parts/ContentLayout';
import CtaButton from '../_parts/CtaButton';
import {
    clearLinkedPartialModels,
    closeSaveAssemblyDialog,
    resetSelectedDocument,
    saveAssemblyDocument,
} from '../../../../redux/project/documents/sliceProjectAssembly';
import AssemblyDialogContent from './_parts/AssemblyDialogContent';

const initialMainState: IAssemblyMainState = {
    subjectText: null,

    selectedDocIdDocIndices: [],

    assemblyId: null,

    index: null,
};

export default () => {
    const { t } = useTranslation('translations', { keyPrefix: 'assemblyDialog' });
    const dispatch: Dispatch<unknown> = useDispatch();

    const { replicaId } = useParams();

    const { loading: loadingPartialModels } = useSelector(
        (_store: IAppReduxStore) => _store.projectSaveAssembly.linkedPartialModels,
    );

    const {
        isSaveAssemblyDialogOpen,
        loading: loadingSaveAssembly,
        selectedDocument,
    } = useSelector((store: IAppReduxStore) => store.projectSaveAssembly);

    const { loading: docTypeIndicesLoading, data: docTypeIndices } = useSelector(
        (store: IAppReduxStore) => store.projectDocTypes.docTypeIndicesData,
    );

    const [mainState, setMainState] = useState<IAssemblyMainState>(initialMainState);

    useEffect(() => {
        if (docTypeIndices?.DocTypeIndices?.length > 0) {
            setMainState(prevState => ({
                ...prevState,
                ['index']: docTypeIndices.DocTypeIndices[0],
            }));
        }
    }, [docTypeIndices]);

    useEffect(() => {
        if (selectedDocument) {
            setMainState(prevState => ({
                ...prevState,
                ['assemblyId']: selectedDocument.IdDocIndex,
                ['subjectText']: selectedDocument.Subject,
            }));
        }
    }, [selectedDocument]);

    const handleChange = ({ field, value }: { field: string; value: string }) => {
        setMainState(prevState => ({
            ...prevState,
            [field]: value,
        }));
    };

    const resetState = () => {
        setMainState(initialMainState);

        dispatch(resetSelectedDocument());
    };

    const onClose = () => {
        resetState();
        dispatch(closeSaveAssemblyDialog());
        dispatch(clearLinkedPartialModels());
    };

    const handleConfirm = () => {
        dispatch(
            saveAssemblyDocument({
                replicaId,
                subject: mainState.subjectText,
                linkedModelsIds: mainState.selectedDocIdDocIndices,
                assemblyId: mainState.assemblyId,
            }),
        );
    };

    return (
        <DialogFrame
            customSx={{ maxHeight: '800px', minHeight: '560px', blockSize: 'auto' }}
            open={isSaveAssemblyDialogOpen}
        >
            <DialogHeader onClose={onClose} dialogTitle={t('dialogTitle')} />
            <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
                <ContentLayout
                    loading={loadingPartialModels || loadingSaveAssembly || docTypeIndicesLoading}
                    main={
                        <AssemblyDialogContent mainState={mainState} handleChange={handleChange} />
                    }
                />
            </DialogContent>

            <Divider />

            <DialogActions sx={{ paddingRight: '24px' }}>
                <CtaButton text={t('cancel')} variant='text' onClick={onClose} />
                <CtaButton
                    text={t('confirm')}
                    disabled={
                        mainState?.selectedDocIdDocIndices?.length < 1 ||
                        docTypeIndicesLoading ||
                        loadingPartialModels ||
                        loadingSaveAssembly
                    }
                    variant='contained'
                    onClick={handleConfirm}
                />
            </DialogActions>
        </DialogFrame>
    );
};

export interface ISaveAssemblyContentProps {
    mainState: IAssemblyMainState;
    handleChange: ({ field, value }: { field: string; value: string | string[] }) => void;
}

export interface IAssemblyMainState {
    subjectText: string;

    selectedDocIdDocIndices: string[];

    assemblyId?: string;

    index?: string;
}
